import * as THREE from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';

/**
 * 3D模型预览工具
 * 使用EdgesGeometry提取模型边缘线，生成鸟瞰图线稿
 */

// 缓存已加载的模型轮廓
const modelOutlineCache = new Map<string, { lines: { x: number; y: number }[][]; width: number; height: number }>();

// 缓存大小限制（最多缓存50个模型轮廓）
const MAX_CACHE_SIZE = 50;

// 清理最旧的缓存项
function cleanOldestCache() {
  if (modelOutlineCache.size >= MAX_CACHE_SIZE) {
    const firstKey = modelOutlineCache.keys().next().value;
    if (firstKey) {
      modelOutlineCache.delete(firstKey);
      console.log('🗑️ 清理模型轮廓缓存:', firstKey);
    }
  }
}

// 清空所有缓存（用于内存清理）
export function clearModelOutlineCache() {
  modelOutlineCache.clear();
  console.log('🗑️ 清空所有模型轮廓缓存');
}

/**
 * 加载3D模型并生成鸟瞰图边缘线
 */
export async function loadModel3DOutline(
  modelPath: string,
  targetWidth: number,
  targetHeight: number
): Promise<{ lines: { x: number; y: number }[][]; width: number; height: number } | null> {
  // 检查缓存（简化缓存key，只使用modelPath，忽略尺寸差异）
  // 这样可以大幅减少缓存数量，因为同一个模型的不同尺寸可以共享轮廓数据
  const cacheKey = modelPath;
  if (modelOutlineCache.has(cacheKey)) {
    return modelOutlineCache.get(cacheKey)!;
  }
  
  // 检查缓存大小，必要时清理
  cleanOldestCache();

  try {
    // 根据文件扩展名选择加载器
    const ext = modelPath.split('.').pop()?.toLowerCase();
    let object: THREE.Object3D | null = null;

    if (ext === 'obj') {
      const loader = new OBJLoader();
      object = await new Promise<THREE.Group>((resolve, reject) => {
        loader.load(
          modelPath,
          (obj: THREE.Group) => resolve(obj),
          undefined,
          (error: unknown) => reject(error)
        );
      });
    } else if (ext === 'gltf' || ext === 'glb') {
      const loader = new GLTFLoader();
      const gltf = await new Promise<any>((resolve, reject) => {
        loader.load(
          modelPath,
          (gltf: any) => resolve(gltf),
          undefined,
          (error: unknown) => reject(error)
        );
      });
      object = gltf.scene;
    } else if (ext === 'fbx') {
      const loader = new FBXLoader();
      object = await new Promise<THREE.Group>((resolve, reject) => {
        loader.load(
          modelPath,
          (fbx: THREE.Group) => {
            console.log('✅ FBX模型轮廓加载成功:', modelPath);
            resolve(fbx);
          },
          undefined,
          (error: unknown) => {
            console.error('❌ FBX模型轮廓加载失败:', modelPath, error);
            reject(error);
          }
        );
      });
    } else {
      console.warn(`Unsupported model format: ${ext}`);
      return null;
    }

    if (!object) return null;

    // 计算模型边界框
    const box = new THREE.Box3().setFromObject(object);
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);

    // 提取顶视图边缘线
    const edgeLines = extractTopViewEdges(object, box, targetWidth, targetHeight);

    const result = {
      lines: edgeLines,
      width: targetWidth,
      height: targetHeight,
    };

    // 释放Three.js对象内存
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });

    // 缓存结果
    modelOutlineCache.set(cacheKey, result);
    console.log(`✅ 模型轮廓已缓存: ${modelPath} (${edgeLines.length} 条线段)`);

    return result;
  } catch (error) {
    console.error('Failed to load 3D model outline:', error);
    return null;
  }
}

/**
 * 从3D模型提取顶视图边缘线（只提取外轮廓，简化内部结构）
 */
function extractTopViewEdges(
  object: THREE.Object3D,
  boundingBox: THREE.Box3,
  targetWidth: number,
  targetHeight: number
): { x: number; y: number }[][] {
  const lines: { x: number; y: number }[][] = [];
  
  // 计算模型尺寸
  const size = new THREE.Vector3();
  boundingBox.getSize(size);
  const center = new THREE.Vector3();
  boundingBox.getCenter(center);

  // 计算缩放比例（使用统一缩放保持模型原始比例）
  // targetWidth和targetHeight是根据真实尺寸和校准比例计算的canvas尺寸
  // 我们需要计算一个统一的缩放因子，让模型的边界框匹配目标尺寸
  // 
  // 策略：计算X和Z方向的缩放因子，取平均值作为统一缩放
  // 这样可以保持模型比例，同时尽可能接近目标尺寸
  const scaleX = size.x > 0 ? targetWidth / size.x : 1;
  const scaleZ = size.z > 0 ? targetHeight / size.z : 1;
  const scale = (scaleX + scaleZ) / 2; // 统一缩放，保持比例
  
  console.log('📐 轮廓缩放计算:', {
    modelSize: { x: size.x.toFixed(3), z: size.z.toFixed(3), unit: '模型单位' },
    targetSize: { width: targetWidth.toFixed(2), height: targetHeight.toFixed(2), unit: 'px' },
    scaleFactors: { x: scaleX.toFixed(3), z: scaleZ.toFixed(3) },
    uniformScale: scale.toFixed(3)
  });

  // 收集所有2D投影点
  const projectedEdges: { x: number; y: number }[][] = [];

  // 遍历所有网格，使用EdgesGeometry提取边缘
  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const geometry = child.geometry;
      
      // 使用更大的阈值角度（45度），只提取明显的外部边缘
      // 角度越大，提取的边缘越少，线条越简化
      const edges = new THREE.EdgesGeometry(geometry, 45);
      const positions = edges.attributes.position;
      
      // 获取世界变换矩阵
      const worldMatrix = child.matrixWorld;
      
      // 提取边缘线段
      for (let i = 0; i < positions.count; i += 2) {
        // 获取线段的两个端点
        const p0 = new THREE.Vector3(
          positions.getX(i),
          positions.getY(i),
          positions.getZ(i)
        );
        const p1 = new THREE.Vector3(
          positions.getX(i + 1),
          positions.getY(i + 1),
          positions.getZ(i + 1)
        );
        
        // 应用世界变换
        p0.applyMatrix4(worldMatrix);
        p1.applyMatrix4(worldMatrix);
        
        // 转换到画布坐标（顶视图：X轴→Canvas X，Z轴→Canvas Y）
        // 使用统一缩放保持模型原始比例
        const canvasP0 = {
          x: (p0.x - center.x) * scale + targetWidth / 2,
          y: (p0.z - center.z) * scale + targetHeight / 2,
        };
        const canvasP1 = {
          x: (p1.x - center.x) * scale + targetWidth / 2,
          y: (p1.z - center.z) * scale + targetHeight / 2,
        };
        
        projectedEdges.push([canvasP0, canvasP1]);
      }
      
      // 清理EdgesGeometry
      edges.dispose();
    }
  });

  // 如果提取到边缘，进一步简化：只保留外围边缘
  if (projectedEdges.length > 0) {
    // 简单策略：只保留距离中心较远的边缘（外轮廓）
    const centerPoint = { x: targetWidth / 2, y: targetHeight / 2 };
    const avgDistance = projectedEdges.reduce((sum, line) => {
      const d0 = Math.hypot(line[0].x - centerPoint.x, line[0].y - centerPoint.y);
      const d1 = Math.hypot(line[1].x - centerPoint.x, line[1].y - centerPoint.y);
      return sum + (d0 + d1) / 2;
    }, 0) / projectedEdges.length;
    
    // 只保留距离大于平均值的边缘（外围边缘）
    // 提高阈值可以进一步简化线条，只保留最外层轮廓
    const threshold = avgDistance * 0.7;
    projectedEdges.forEach((line) => {
      const d0 = Math.hypot(line[0].x - centerPoint.x, line[0].y - centerPoint.y);
      const d1 = Math.hypot(line[1].x - centerPoint.x, line[1].y - centerPoint.y);
      const avgDist = (d0 + d1) / 2;
      
      if (avgDist >= threshold) {
        lines.push(line);
      }
    });
  }

  // 如果没有提取到边缘，返回简单的矩形
  if (lines.length === 0) {
    const margin = 10;
    lines.push(
      [
        { x: margin, y: margin },
        { x: targetWidth - margin, y: margin },
      ],
      [
        { x: targetWidth - margin, y: margin },
        { x: targetWidth - margin, y: targetHeight - margin },
      ],
      [
        { x: targetWidth - margin, y: targetHeight - margin },
        { x: margin, y: targetHeight - margin },
      ],
      [
        { x: margin, y: targetHeight - margin },
        { x: margin, y: margin },
      ]
    );
  }

  console.log(`🎨 轮廓简化: ${projectedEdges.length} → ${lines.length} 条线段`);

  return lines;
}

/**
 * 在Canvas上绘制模型边缘线
 */
export function drawModel3DOutline(
  ctx: CanvasRenderingContext2D,
  outline: { lines: { x: number; y: number }[][]; width: number; height: number },
  centerX: number,
  centerY: number,
  color: string = '#3b82f6',
  rotation: number = 0
) {
  ctx.save();
  
  // 应用旋转
  if (rotation) {
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);
  }
  
  // 计算偏移量（使轮廓居中）
  const offsetX = centerX - outline.width / 2;
  const offsetY = centerY - outline.height / 2;
  
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  
  // 绘制所有线段
  outline.lines.forEach((line) => {
    if (line.length < 2) return;
    
    ctx.beginPath();
    ctx.moveTo(line[0].x + offsetX, line[0].y + offsetY);
    ctx.lineTo(line[1].x + offsetX, line[1].y + offsetY);
    ctx.stroke();
  });
  
  ctx.restore();
}
